import React, { useState, useEffect } from 'react';

// Material components
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import IconButton from '@mui/material/IconButton';
import EditIcon from '@mui/icons-material/Edit';
import CancelIcon from '@mui/icons-material/Cancel';
import Snackbar from '@mui/material/Snackbar';
import MuiAlert from '@mui/material/Alert';

// Resources
import { IStation } from '../../interfaces/station';
import { IAlert } from '../../interfaces/alert';
import { getStations, stationsAPI } from '../../api/stations';

// Assets
import { useStyles } from './styles';

const Stations = () => {
	const [station, setStation] = useState<IStation>({} as IStation);
	const [edit, setEdit] = useState(false);
	const [alert, setAlert] = useState<IAlert>({
		visible: false,
		message: '',
		severity: 'success',
	});
	const [stations, setStations] = useState<IStation[]>([]);
	const classes = useStyles();

	useEffect(() => {
		_getDataFromApi();
	}, []);

	const _getDataFromApi = (): void => {
		getStations().then(res => {
			setStations(res.reverse() || []);
		});
	};

	const _handleSaveStation = (): void => {
		if (!station.name) {
			return setAlert({
				visible: true,
				severity: 'warning',
				message: 'Please add a name to the station',
			});
		}

		let promise: Promise<IStation>;

		if (edit) {
			promise = stationsAPI.put(station.id, station);
		} else {
			promise = stationsAPI.post('', station);
		}

		promise
			.then(res => {
				if (res) {
					setAlert({
						visible: true,
						severity: 'success',
						message: 'The station has been saved successfully',
					});
					_getDataFromApi();
				}
			})
			.catch(() => {
				setAlert({
					visible: true,
					severity: 'error',
					message: 'Error, the station has not been saved',
				});
			})
			.finally(() => _cleanStates());
	};

	const _cleanStates = (): void => {
		setStation({} as IStation);
		setEdit(false);
	};

	return (
		<div className={classes.containerGroup}>
			<div className={classes.containerFormGroup}>
				<TextField
					id="name"
					label="Name"
					className={classes.textField}
					variant="outlined"
					fullWidth
					value={station.name || ''}
					onChange={e => setStation({ ...station, name: e.target.value })}
				/>
				<Button
					className={classes.btnSave}
					variant="contained"
					onClick={_handleSaveStation}
				>
					{edit ? 'Edit' : 'Save'}
				</Button>
				{edit && (
					<IconButton
						size="large"
						edge="start"
						color="inherit"
						className={classes.btnCleanStates}
						aria-label="menu"
						sx={{ mr: 2 }}
						onClick={_cleanStates}
					>
						<CancelIcon />
					</IconButton>
				)}
			</div>
			<TableContainer component={Paper}>
				<Table sx={{ minWidth: 650 }} aria-label="simple table">
					<TableHead className={classes.headerTable}>
						<TableRow>
							<TableCell className={classes.headerTextTable}>Name</TableCell>
							<TableCell
								size="small"
								className={classes.headerTextTableActions}
							>
								Actions
							</TableCell>
						</TableRow>
					</TableHead>
					<TableBody>
						{stations.map(row => (
							<TableRow
								key={row.id}
								sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
							>
								<TableCell align="left">{row.name}</TableCell>
								<TableCell align="left">
									<IconButton
										size="large"
										edge="start"
										color="inherit"
										aria-label="menu"
										sx={{ mr: 2 }}
										onClick={() => {
											setStation({ ...row });
											setEdit(true);
										}}
									>
										<EditIcon />
									</IconButton>
								</TableCell>
							</TableRow>
						))}
					</TableBody>
				</Table>
			</TableContainer>
			<Snackbar
				anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
				open={alert.visible}
				onClose={() => setAlert({ ...alert, visible: false })}
			>
				<MuiAlert
					elevation={6}
					severity={alert.severity}
					variant="filled"
					onClose={() => setAlert({ ...alert, visible: false })}
					sx={{ width: '100%' }}
				>
					{alert.message}
				</MuiAlert>
			</Snackbar>
		</div>
	);
};

export default Stations;
